import AuthHttpService from "./AuthHttpService";

import {
  ICreatorsItemsList,
  IGifList,
  IAddToHistory,
  INichesItems,
  ISearchNicheList,
  ISearchUsersList,
  ITagsList,
  THistoryList,
} from "./types";

interface ISearchGifsRequest {
  query: string;
  page: number;
  count: number;
  order: "latest" | "trending" | "top" | "top28";
  verified?: boolean;
}

interface ISearchNichesRequest {
  query: string;
  page: number;
  count: number;
  order: "posts" | "subscribers";
}

interface ISearchCreatorsRequest {
  query: string;
  page: number;
  count: number;
  order: "latest" | "trending" | "top";
  verified?: boolean;
}

export default class SearchService extends AuthHttpService {
  public constructor(readonly url?: string) {
    super(url);
  }

  async searchGifs({ query, page, count, order, verified }: ISearchGifsRequest): Promise<IGifList> {
    const params = new URLSearchParams({
      query,
      page: page.toString(),
      count: count.toString(),
      order
    });

    verified && params.append("verified", "yes");

    return await this.get(`v2/search/gifs?${params.toString()}`, {
      "X-Session-Id": await this.getSessionId(),
    });
  }

  async searchImages({ query, page, count, order, verified }: ISearchGifsRequest): Promise<IGifList> {
    const params = new URLSearchParams({
      query,
      page: page.toString(),
      count: count.toString(),
      order,
    });

    verified && params.append("verified", "yes");

    return await this.get(`v2/search/images?${params.toString()}`);
  }

  async searchNiches({ query, page, count, order }: ISearchNichesRequest): Promise<ISearchNicheList> {
    const params = new URLSearchParams({
      query,
      page: page.toString(),
      count: count.toString(),
      order,
    });

    return await this.get(`v2/search/niches?${params.toString()}`);
  }

  async searchCreators({ query, page, count, order, verified }: ISearchCreatorsRequest): Promise<ISearchUsersList> {
    const params = new URLSearchParams({
      query,
      page: page.toString(),
      count: count.toString(),
      order,
    });

    verified && params.append("verified", "yes");

    return await this.get(`v2/search/creators?${params.toString()}`);
  }

  async getCreatorsSearches(query: string): Promise<ICreatorsItemsList> {
    return await this.get(`v2/creators/suggest?query=${query}`);
  }

  async getNichesSearches(query: string): Promise<INichesItems[]> {
    return await this.get(`v2/niches/search?query=${query}`);
  }

  async getTagsSearches(query: string): Promise<ITagsList[]> {
    return await this.get(`v2/search/suggest?query=${query}`);
  }

  async getHistorySearches(): Promise<THistoryList> {
    return await this.get("v2/search/user-history");
  }

  async removeAllHistorySearches(): Promise<void> {
    return await this.delete("v2/search/user-history");
  }

  async removeHistorySearchItem(id: string): Promise<void> {
    return await this.delete(`v2/search/user-history/${id}`);
  }

  async addHistorySearchItem(data: IAddToHistory): Promise<void> {
    return await this.post("v2/search/user-history", data);
  }

  async getTopThisWeek(): Promise<IGifList> {
    return await this.get("v2/explore/top-this-week");
  }

  async getTrendingGifs(): Promise<IGifList> {
    return await this.get("v2/explore/trending-gifs");
  }

  async getTrendingImages(): Promise<IGifList> {
    return await this.get("v2/explore/trending-images");
  }
}
